import {
  blade,
  cloud,
  dots,
  figure,
  footprint,
  groundRing,
  gust,
  shards,
  slash,
  snowflake,
  sword,
  target,
} from '../kit';
import { C, mix, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * กระบี่ธารเยือกแข็ง (soft): a pale silver sword with an ice-blue hilt, snow and frost. เหมันต์สะกดธาร,
 * its ultimate, is a starburst of eight warping cuts round a snowflake.
 */

const HILT = '#7ad0f0';
const FROST = '#d8f4ff';
const TASSEL = '#e8f8ff';

function snowSword(p: Pix, x0: number, y0: number, x1: number, y1: number): void {
  sword(p, x0, y0, x1, y1, { hilt: HILT, blade: '#eef6ff', tassel: TASSEL });
}

/** A tall six-sided ice crystal. */
function crystal(p: Pix, cx: number, y: number, w: number, h: number): void {
  p.poly(
    [
      [cx, y],
      [cx + w / 2, y + w / 2],
      [cx + w / 2, y + h - w / 2],
      [cx, y + h],
      [cx - w / 2, y + h - w / 2],
      [cx - w / 2, y + w / 2],
    ],
    C.ice,
  );
  p.line(cx, y + 1, cx, y + h - 1, FROST);
  p.line(cx + 1, y + 2, cx + w / 2 - 1, y + w / 2 + 1, C.white);
}

registerIcons('skill', {
  // A cut that leaves snow behind it, the flakes growing as the daze builds.
  'ss-shift': (p) => {
    slash(p, 15, 17, 13, -2.6, 0.2, FROST, 2);
    snowSword(p, 5, 27, 25, 7);
    snowflake(p, 6, 8, 2, C.white, false);
    snowflake(p, 13, 4, 3, C.white, false);
    snowflake(p, 25, 18, 4, C.white);
  },
  // Frost afterimages leaping onto a marked foe.
  'ss-frost': (p) => {
    target(p, 25, 7, 3, C.white);
    figure(p, 2, 17, mix(HILT, C.ink, 0.45), 'leap');
    figure(p, 8, 13, mix(HILT, C.ink, 0.15), 'leap');
    figure(p, 14, 9, C.white, 'leap', { head: FROST });
    for (const x of [4, 10, 16, 22]) p.line(x, 29, x + 2, 25, C.ice).px(x + 2, 25, C.white);
  },
  // A snow cloud pouring a blizzard onto a ring on the ground.
  'ss-gale': (p) => {
    groundRing(p, 16, 25, 12, 4, C.ice);
    for (let k = 0; k < 7; k++)
      p.line(8 + k * 3, 11 + (k % 2) * 2, 4 + k * 3, 19 + (k % 3) * 2, k % 2 ? C.white : C.ice);
    cloud(p, 7, 10, 18, '#dce8f4');
    p.rect(8, 9, 16, 1, '#b8c8dc');
    snowflake(p, 16, 21, 3, C.white, false);
    snowflake(p, 26, 15, 2, C.white, false);
    snowflake(p, 5, 16, 2, C.white, false);
  },
  // Footprints racing over snow, light as the wind.
  'ss-walk': (p) => {
    p.ell(16, 26, 14, 4, '#e8f4ff');
    footprint(p, 6, 17, C.iceDark);
    footprint(p, 14, 11, C.iceDark, true);
    footprint(p, 22, 5, C.iceDark);
    gust(p, 3, 13, 10, C.white, false);
    gust(p, 12, 28, 12, C.white);
  },
  // A sword point shatters a pillar of ice.
  'ss-ice': (p) => {
    crystal(p, 11, 3, 10, 22);
    p.path(
      [
        [12, 4],
        [9, 10],
        [13, 14],
        [10, 20],
        [12, 24],
      ],
      C.iceDark,
    );
    shards(p, 11, 14, 9, C.white, 5, 0.6);
    blade(p, 29, 4, 15, 12, { w: 2.2, col: '#eef6ff' });
    p.px(14, 12, C.white);
  },
  // Upright against the wind: the sword stands in the snow, the gale streaming past it.
  'ss-pride': (p) => {
    p.ell(17, 27, 11, 3.5, C.paper);
    blade(p, 17, 9, 17, 27, { w: 3, col: '#eef6ff' });
    p.thick(12, 8, 22, 8, HILT, 2);
    p.rect(16, 3, 2, 5, C.woodDark);
    p.rect(15, 2, 4, 1, HILT);
    gust(p, 3, 14, 9, C.white);
    gust(p, 20, 20, 8, C.white, false);
    snowflake(p, 25, 10, 3, C.white, false);
    snowflake(p, 6, 22, 2, C.white, false);
  },
  // A cut that leaves no trace: one long streak, the sword already gone past.
  'ss-trace': (p) => {
    dots(p, 2, 24, 16, 16, FROST, 3);
    p.line(8, 21, 28, 9, C.white);
    p.line(9, 21, 28, 10, FROST);
    snowSword(p, 17, 21, 29, 5);
    snowflake(p, 6, 10, 3, C.white, false);
  },
  // เหมันต์สะกดธาร: eight warping cuts crossing in a starburst round a snowflake, two of them red.
  'ss-hatred': (p) => {
    for (let k = 0; k < 8; k++) {
      const a = (k * Math.PI) / 4 + 0.2;
      const r = k % 2 ? 11 : 13;
      p.line(
        16 - Math.cos(a) * r,
        16 - Math.sin(a) * r,
        16 + Math.cos(a) * r,
        16 + Math.sin(a) * r,
        k === 1 || k === 5 ? '#ff5a6a' : k % 2 ? FROST : C.white,
      );
    }
    p.ell(16, 16, 5, 5, mix(HILT, C.ink, 0.35));
    snowflake(p, 16, 16, 5, C.white);
  },
});
